import { useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Linkedin, Link2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { fetchArticle, fetchMonetization } from "@/lib/api";
import { ArticleCover } from "@/components/ArticleCover";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { InArticleAd } from "@/components/AdSlot";
import { Newsletter } from "@/components/Newsletter";
import { AFFILIATE_TOOLS } from "@/config/monetization";
import { useSEO } from "@/hooks/useSEO";
import { Comments } from "@/components/Comments";

const formatDate = (iso) =>
    new Date(iso + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });

const Block = ({ block }) => {
    if (block.type === "h2") {
        return <h2 className="ab-h2">{block.text}</h2>;
    }
    if (block.type === "quote") {
        return <blockquote className="ab-quote">{block.text}</blockquote>;
    }
    if (block.type === "list") {
        return (
            <ul className="ab-list">
                {block.items.map((item, i) => (
                    <li key={i}>{item}</li>
                ))}
            </ul>
        );
    }
    return <p className="ab-p">{block.text}</p>;
};

const ASPECT_CLASS = { "16:9": "aspect-video", "21:9": "aspect-[21/9]", "4:3": "aspect-[4/3]" };
const LEADING_VAR = { compacta: 1.6, normal: 1.85, ampla: 2.1 };
const SPACING_VAR = { compacto: "0.75rem", normal: "1.25rem", amplo: "2rem" };
const LEGACY_PAD_PX = { sem: 0, normal: 16, ampla: 40 };

export const padPx = (article) => {
    const legacy = LEGACY_PAD_PX[article?.text_padding] ?? 0;
    const left = article?.text_indent_left ?? 0;
    const right = article?.text_indent_right ?? 0;
    return {
        left: left > 0 ? left : legacy,
        right: right > 0 ? right : legacy,
    };
};

const AffiliateCallout = () => {
    const { data: monetization } = useQuery({ queryKey: ["monetization"], queryFn: fetchMonetization });
    const tools = monetization?.affiliate_tools?.length ? monetization.affiliate_tools : AFFILIATE_TOOLS;
    const tool = tools[1] || tools[0];
    return (
        <Reveal>
            <a
                href={tool.url}
                target="_blank"
                rel="sponsored noopener noreferrer"
                data-testid="article-affiliate-callout"
                className="group my-10 flex items-center gap-5 border border-gold/30 bg-gold/[0.04] hover:border-gold/60 p-6 transition-colors"
            >
                <div className="flex-1">
                    <span className="text-[9px] uppercase tracking-[0.25em] text-gold/70">Link de afiliado</span>
                    <p className="font-serif text-lg md:text-xl mt-1.5 group-hover:text-gold transition-colors">{tool.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{tool.description}</p>
                </div>
                <ArrowUpRight size={20} className="text-gold shrink-0" />
            </a>
        </Reveal>
    );
};

export default function ArticlePage() {
    const { slug } = useParams();
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll();
    const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const yCover = useTransform(heroProgress, [0, 1], [0, 120]);

    const { data: article, isLoading, isError } = useQuery({
        queryKey: ["article", slug],
        queryFn: () => fetchArticle(slug),
        retry: false,
    });

    useSEO({
        title: article ? `${article.title} — Investidor Comum` : "Investidor Comum",
        description: article?.excerpt,
        url: window.location.href,
        type: "article",
    });

    const copyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        toast.success("Link copiado para a área de transferência.");
    };

    if (isLoading) {
        return (
            <main data-testid="article-loading" className="min-h-screen pt-32 max-w-3xl mx-auto px-4">
                <div className="h-4 w-32 bg-white/5 animate-pulse mb-6" />
                <div className="h-12 w-full bg-white/5 animate-pulse mb-3" />
                <div className="h-12 w-2/3 bg-white/5 animate-pulse mb-10" />
                <div className="h-64 w-full bg-white/5 animate-pulse" />
            </main>
        );
    }

    if (isError || !article) {
        return (
            <main data-testid="article-not-found" className="min-h-screen pt-40 max-w-3xl mx-auto px-4 text-center">
                <p className="font-serif text-4xl italic text-slate-400">Artigo não encontrado.</p>
                <Link to="/" data-testid="article-back-home" className="inline-flex items-center gap-2 mt-8 text-gold hover:text-golddark transition-colors text-sm uppercase tracking-[0.2em]">
                    <ArrowLeft size={15} /> Voltar para a home
                </Link>
            </main>
        );
    }

    const shareUrl = encodeURIComponent(window.location.href);
    const shareTitle = encodeURIComponent(article.title);
    const content = article.content || [];
    const adAfter = 3;
    const calloutAfter = Math.max(adAfter + 2, Math.floor(content.length * 0.65));
    const pad = padPx(article);

    return (
        <main data-testid="article-page">
            <motion.div
                data-testid="reading-progress"
                className="fixed top-0 left-0 right-0 h-[3px] bg-gold z-[60] origin-left"
                style={{ scaleX: scrollYProgress }}
            />

            <article className="pt-28 md:pt-36">
                <div className="max-w-[800px] mx-auto px-4 md:px-6">
                    <Link to="/" data-testid="article-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-gold transition-colors mb-8">
                        <ArrowLeft size={14} /> Todos os artigos
                    </Link>

                    <span data-testid="article-category" className="block text-xs uppercase tracking-[0.3em] text-gold font-medium mb-4">
                        {article.category}
                    </span>
                    <h1 data-testid="article-title" className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
                        <MaskedLine delay={0.1}>{article.title}</MaskedLine>
                    </h1>

                    <div data-testid="article-meta" className="mt-8 flex flex-wrap items-center gap-4 pb-8 border-b border-white/10">
                        <span className="flex items-center justify-center w-10 h-10 rounded-full bg-gold/10 border border-gold/30 text-gold font-serif text-sm">
                            {article.author.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                        </span>
                        <div>
                            <p className="text-sm font-medium">{article.author}</p>
                            <p className="text-xs text-slate-500">{article.author_role}</p>
                        </div>
                        <span className="ml-auto text-xs text-slate-500">
                            {formatDate(article.published_at)} · {article.read_minutes} min de leitura
                        </span>
                    </div>
                </div>

                <div ref={heroRef} className="max-w-5xl mx-auto px-4 md:px-8 mt-10">
                    <motion.div style={{ y: yCover }} className={`border border-white/10 overflow-hidden ${ASPECT_CLASS[article.cover_aspect] || "aspect-video"}`}>
                        <ArticleCover article={article} />
                    </motion.div>
                </div>

                <div className="max-w-[800px] mx-auto px-4 md:px-6 mt-10">
                    <p data-testid="article-excerpt" className="font-serif text-xl md:text-2xl text-slate-300 leading-relaxed italic border-l-2 border-white/20 pl-6">
                        {article.excerpt}
                    </p>

                    <div
                        data-testid="article-content"
                        className="article-body mt-4"
                        style={{
                            "--rh-leading": LEADING_VAR[article.text_leading] || 1.85,
                            "--rh-gap": SPACING_VAR[article.text_spacing] || "1.25rem",
                            "--rh-pad-l": `${pad.left}px`,
                            "--rh-pad-r": `${pad.right}px`,
                        }}
                    >
                        {article.content_html ? (
                            <div className="rich-content" dangerouslySetInnerHTML={{ __html: article.content_html }} />
                        ) : (
                            content.map((block, i) => (
                                <div key={i}>
                                    <Block block={block} />
                                    {i === adAfter && <InArticleAd />}
                                    {i === calloutAfter && <AffiliateCallout />}
                                </div>
                            ))
                        )}
                        {article.content_html && <InArticleAd />}
                        {article.content_html && <AffiliateCallout />}
                    </div>

                    <div className="mt-14 pt-8 border-t border-white/10">
                        <span className="text-xs uppercase tracking-[0.25em] text-slate-500">Compartilhar</span>
                        <div className="mt-4 flex gap-3">
                            <a data-testid="share-whatsapp" href={`https://wa.me/?text=${shareTitle}%20${shareUrl}`} target="_blank" rel="noopener noreferrer"
                                className="px-4 py-2 border border-white/15 text-xs uppercase tracking-wider text-slate-300 hover:border-gold hover:text-gold transition-colors">
                                WhatsApp
                            </a>
                            <a data-testid="share-linkedin" href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 border border-white/15 text-xs uppercase tracking-wider text-slate-300 hover:border-gold hover:text-gold transition-colors">
                                <Linkedin size={13} /> LinkedIn
                            </a>
                            <button data-testid="share-copy-link" onClick={copyLink}
                                className="flex items-center gap-2 px-4 py-2 border border-white/15 text-xs uppercase tracking-wider text-slate-300 hover:border-gold hover:text-gold transition-colors">
                                <Link2 size={13} /> Copiar link
                            </button>
                        </div>
                    </div>
                </div>

                <Comments slug={article.slug} />

                {article.related && article.related.length > 0 && (
                    <div className="max-w-7xl mx-auto px-4 md:px-8 mt-20 md:mt-28">
                        <Reveal>
                            <h2 className="font-serif text-2xl md:text-3xl tracking-tight mb-8">Continue lendo</h2>
                        </Reveal>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {article.related.map((r, i) => (
                                <Reveal key={r.slug} delay={i * 0.08}>
                                    <Link to={`/artigo/${r.slug}`} data-testid={`related-article-${i}`} className="group block border border-white/10 hover:border-white/25 transition-colors bg-paper">
                                        <div className="overflow-hidden h-36">
                                            <div className="h-full transition-transform duration-700 group-hover:scale-105">
                                                <ArticleCover article={r} />
                                            </div>
                                        </div>
                                        <div className="p-5">
                                            <span className="text-[10px] uppercase tracking-[0.2em] text-gold">{r.category}</span>
                                            <h3 className="font-serif text-base leading-snug mt-2 group-hover:text-gold transition-colors clamp-2">{r.title}</h3>
                                        </div>
                                    </Link>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                )}
            </article>

            <div className="mt-20 md:mt-28">
                <Newsletter />
            </div>
        </main>
    );
}
